// src/App.js
import React, { useEffect, useState } from "react";
import { Amplify, Auth, Hub } from "aws-amplify";
import { BrowserRouter as Router, Switch, Route, Redirect } from "react-router-dom";
import "./App.scss";
import "bootstrap/dist/css/bootstrap.min.css";

import amplifyConfig from "./amplifyConfig";
import GlobalErrorBoundary from "./errorboundaries";
import { ModalContextProvider } from "contexts/Modal";
import { HistoryContextProvider } from "contexts/History";
import { LoggedInUserContextProvider } from "./contexts/LoggedInUser";
import { SidePanelContextProvider } from "contexts/SidePanel";

import LoginChooser from "./components/auth/LoginChooser";
import ForgotStart from "./components/auth/ForgotStart";
import ResetPassword from "./components/auth/ResetPassword";
import Dashboard from "./components/Dashboard"; // Dashboard renders its own routes
import { cleanupWrongCognitoCookies /*, cleanupLegacyCognitoCookies */ } from "./cleanupCookies";

// If you ever want to force-clear *all* old cognito cookies, you can call:
// cleanupLegacyCognitoCookies();
cleanupWrongCognitoCookies(
  amplifyConfig.Auth.userPoolWebClientId,
  amplifyConfig.Auth.userPoolId
);


Amplify.configure(amplifyConfig);

function useCurrentUser() {
  const [checked, setChecked] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const u = await Auth.currentAuthenticatedUser();
        if (mounted) setUser(u || null);
      } catch {
        if (mounted) setUser(null);
      } finally {
        if (mounted) setChecked(true);
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const unsub = Hub.listen("auth", async ({ payload }) => {
      const evt = payload?.event;
      if (evt === "signIn" || evt === "cognitoHostedUI") {
        try { setUser(await Auth.currentAuthenticatedUser()); } catch { setUser(null); }
      } else if (evt === "signOut") {
        setUser(null);
      }
    });
    return () => { unsub(); };
  }, []);

  return { checked, user };
}

function Providers({ children }) {
  return (
    <GlobalErrorBoundary>
      <ModalContextProvider>
        <LoggedInUserContextProvider>
          <HistoryContextProvider>
              <SidePanelContextProvider>
                {children}
              </SidePanelContextProvider>
          </HistoryContextProvider>
        </LoggedInUserContextProvider>
      </ModalContextProvider>
    </GlobalErrorBoundary>
  );
}

export default function App() {
  const { checked, user } = useCurrentUser();

  if (!checked) return null; // or a small spinner

  return (
    <Router>
      <Providers>
        {user ? (
          // PRIVATE branch: render the app shell; Dashboard includes <AppRoutes/>
          <Dashboard />
        ) : (
          // PUBLIC branch: auth pages only
          <Switch>
            <Route exact path="/signin" component={LoginChooser} />
            <Route path="/auth/forgot" component={ForgotStart} />
            <Route path="/auth/reset" component={ResetPassword} />
            <Redirect to="/signin" />
          </Switch>
        )}
      </Providers>
    </Router>
  );
}
